import { ChatMessage } from '../stores/useChatStore.js';
import { studioFetch } from '../studio/api.js';
import {
    studioEventStream,
    type MissionTimelineEvent as StreamMissionEvent,
} from '../services/hivecore-scene-stream/StudioEventStreamService.js';
import type { ARCOSWebhookEvent as BaseARCOSWebhookEvent } from '../../../src/shared/arcos/webhookTypes.js';

export type { ARCOSWebhookEventType } from '../../../src/shared/arcos/webhookTypes.js';

export interface ARCOSWebhookEvent extends BaseARCOSWebhookEvent {}

export interface WebhookResult {
    message: ChatMessage;
    shouldInject: boolean;
}

/** Cleanup function returned by listenForWebhooks to cancel pending mock events */
export type WebhookCleanup = () => void;

/** Check if mock UI events are enabled via env var (default: false) */
function isMockEventsEnabled(): boolean {
    // VITE_ENABLE_UI_MOCK_EVENTS must be explicitly set to 'true' to enable mock events
    // Default is false - production and staging only show real events
    return (import.meta as any).env?.['VITE_ENABLE_UI_MOCK_EVENTS'] === 'true';
}

/**
 * Timeline event types from backend MissionTimelineService
 */
export type TimelineEventType =
    | 'mission.start'
    | 'mission.finish'
    | 'bee.run'
    | 'file.read'
    | 'file.write'
    | 'command.exec'
    | 'test.run'
    | 'pr.open'
    | 'deploy.start'
    | 'deploy.finish'
    | 'error'
    | 'info'
    | 'artifact'
    | 'step'
    | 'system';

/**
 * Mission timeline event from backend
 */
export interface MissionTimelineEvent {
    id: string;
    missionId: string;
    t: TimelineEventType;
    severity: 'info' | 'warn' | 'error';
    title: string;
    message?: string;
    meta?: Record<string, unknown>;
    createdAt: string;
}

/**
 * Polling state for REST backfill (used only for initial load, session switch, reconnect)
 * Phase 3C: REST polling replaced with Socket.IO push for real-time events
 */
interface _PollingState {
    intervalId: ReturnType<typeof setInterval> | null;
    currentIntervalMs: number;
    lastSeenEventIds: Set<string>;
    lastMissionId: string | null;
    isPolling: boolean;
}

class ARCOSWebhookHandler {
    async injectWebhookResults(
        addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void,
        addFeedback?: (text: string) => void
    ) {
        const mockEvents = [
            {
                type: 'task_completed' as const,
                data: { summary: 'Code analysis completed successfully', beeName: 'CodeAnalyzer' },
            },
            {
                type: 'pr_created' as const,
                data: {
                    number: 1,
                    title: 'feat: implement Beehive workspace',
                    url: 'https://github.com/DDPMLS/beehive-workspace/pull/1',
                },
            },
        ];

        for (const eventData of mockEvents) {
            if (addFeedback && eventData.type === 'task_completed') {
                addFeedback(
                    `[${eventData.data['beeName']}.${eventData.type}] ${eventData.data['summary']}`
                );
            } else {
                const result = await this.simulateWebhookEvent(eventData.type, eventData.data);
                if (result.shouldInject) {
                    addMessage({
                        sender: result.message.sender,
                        content: result.message.content,
                        type: result.message.type,
                        messageType: 'chat',
                        buildCredits: result.message.buildCredits,
                        relatedTaskId: result.message.relatedTaskId,
                    });
                }
            }
        }
    }

    async processWebhookEvent(event: ARCOSWebhookEvent): Promise<WebhookResult> {
        try {
            const message = this.formatEventAsMessage(event);

            try {
                const API_BASE =
                    (import.meta as any).env?.['VITE_BACKEND_URL'] || 'http://localhost:3001';
                const response = await fetch(`${API_BASE}/api/ontology/process-webhook`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(event),
                });

                if (response.ok) {
                    const ontologyResult = await response.json();
                    if (ontologyResult.buildCredits) {
                        message.buildCredits =
                            (message.buildCredits || 0) + ontologyResult.buildCredits;
                    }
                    if (ontologyResult.relatedTaskId) {
                        message.relatedTaskId = ontologyResult.relatedTaskId;
                    }
                }
            } catch (apiError) {
                console.warn(
                    'Ontology API call failed, proceeding with standard processing:',
                    apiError
                );
            }
            return {
                message,
                shouldInject: true,
            };
        } catch (error) {
            console.error('Failed to process webhook event:', error);
            return {
                message: {
                    id: `webhook_error_${Date.now()}`,
                    sender: 'system',
                    content: `Failed to process webhook event: ${event.type}`,
                    type: 'text',
                    timestamp: new Date().toISOString(),
                    buildCredits: 10,
                },
                shouldInject: false,
            };
        }
    }

    private formatEventAsMessage(event: ARCOSWebhookEvent): ChatMessage {
        const baseMessage = {
            id: `webhook_${event.id}`,
            sender: 'bee' as const,
            timestamp:
                event.timestamp instanceof Date ? event.timestamp.toISOString() : event.timestamp,
            relatedTaskId: event.taskId,
        };

        switch (event.type) {
            case 'task_completed':
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: `${event.beeName || 'Bee'} Task Completed`,
                        description: event.data['summary'] || 'Task completed successfully',
                        status: 'completed',
                        data: {
                            ...event.data,
                            // ProofLogPayload enrichments for rich card rendering
                            verificationDepth: event.data['verificationDepth'],
                            executionPolicy: event.data['executionPolicy'],
                            modelTierUsed: event.data['modelTierUsed'],
                            contextPackId: event.data['contextPackId'],
                            proofLogId: event.data['proofLogId'],
                        },
                        actions: ['View Details', 'Recall Output'],
                    }),
                    type: 'pluginCard',
                    buildCredits:
                        Math.ceil(((event.data['summary'] as string) || '').length / 4) + 20,
                };

            case 'task_failed':
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: `${event.beeName || 'Bee'} Task Failed`,
                        description: event.data['error'] || 'Task execution failed',
                        status: 'failed',
                        data: event.data,
                        actions: ['Retry', 'View Logs', 'Debug'],
                    }),
                    type: 'pluginCard',
                    buildCredits:
                        Math.ceil(((event.data['error'] as string) || '').length / 4) + 20,
                };

            case 'pr_created':
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: 'Pull Request Created',
                        description: `PR #${event.data['number']}: ${event.data['title']}`,
                        status: 'completed',
                        data: {
                            url: event.data['url'],
                            number: event.data['number'],
                            title: event.data['title'],
                            branch: event.data['branch'],
                        },
                        actions: ['View PR', 'Review Changes', 'Merge'],
                    }),
                    type: 'pluginCard',
                    buildCredits:
                        Math.ceil(((event.data['title'] as string) || '').length / 4) + 30,
                };

            case 'deployment_success':
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: 'Deployment Successful',
                        description: `Deployed to ${event.data['environment']}`,
                        status: 'completed',
                        data: {
                            environment: event.data['environment'],
                            url: event.data['url'],
                            deploymentId: event.data['deploymentId'],
                            duration: event.data['duration'],
                        },
                        actions: ['View App', 'Check Logs', 'Monitor'],
                    }),
                    type: 'pluginCard',
                    buildCredits: 25,
                };

            case 'ci_status': {
                const isSuccess = event.data['status'] === 'success';
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: `CI ${isSuccess ? 'Passed' : 'Failed'}`,
                        description: `${isSuccess ? '✅' : '❌'} ${event.data['workflow']} ${isSuccess ? 'passed' : 'failed'} in ${event.data['duration']}`,
                        status: isSuccess ? 'completed' : 'failed',
                        data: {
                            ...event.data,
                            // ProofLogPayload enrichments
                            modelTierUsed: event.data['modelTierUsed'],
                            contextPackId: event.data['contextPackId'],
                        },
                        actions: isSuccess ? ['View Results'] : ['View Logs', 'Retry', 'Debug'],
                    }),
                    type: 'pluginCard',
                    buildCredits: 20,
                };
            }

            case 'requires_input':
                return {
                    ...baseMessage,
                    content: JSON.stringify({
                        title: 'Input Required',
                        description: event.data['prompt'] || 'Beehive needs your input to continue',
                        status: 'pending',
                        data: event.data,
                        actions: ['Respond', 'View Details'],
                    }),
                    type: 'pluginCard',
                    buildCredits: 15,
                };

            default:
                return {
                    ...baseMessage,
                    content: `Received ${event.type} event from ARCOS`,
                    type: 'text',
                    buildCredits: 10,
                };
        }
    }

    async simulateWebhookEvent(
        type: ARCOSWebhookEvent['type'],
        data: Record<string, unknown> = {}
    ): Promise<WebhookResult> {
        const mockEvent: ARCOSWebhookEvent = {
            id: `mock_${Date.now()}`,
            type,
            sessionId: `session_${Date.now()}`,
            taskId: `task_${Date.now()}`,
            timestamp: new Date(),
            data,
            beeName: (data['beeName'] as string) || 'Mock-Bee',
        };

        return this.processWebhookEvent(mockEvent);
    }

    /**
     * Map a MissionTimelineEvent to a WebhookResult for card rendering.
     * This is the core mapping function that converts backend timeline events
     * to the card format expected by the UI.
     */
    mapTimelineEventToCard(event: MissionTimelineEvent): WebhookResult {
        const baseMessage = {
            id: `timeline_${event.id}`,
            sender: 'bee' as const,
            timestamp: event.createdAt,
            relatedTaskId: event.missionId,
        };

        // Map timeline event type to card format
        switch (event.t) {
            case 'mission.start':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: 'Mission Started',
                            description: event.message || event.title,
                            status: 'running',
                            data: {
                                missionId: event.missionId,
                                ...event.meta,
                            },
                            actions: ['View Mission', 'View Logs'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 15,
                    },
                    shouldInject: true,
                };

            case 'mission.finish': {
                const isSuccess = event.severity !== 'error';
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: isSuccess ? 'Mission Completed' : 'Mission Failed',
                            description: event.message || event.title,
                            status: isSuccess ? 'completed' : 'failed',
                            data: {
                                missionId: event.missionId,
                                // ProofLogPayload enrichments from meta
                                verificationDepth: event.meta?.['verificationDepth'],
                                executionPolicy: event.meta?.['executionPolicy'],
                                modelTierUsed: event.meta?.['modelTierUsed'],
                                contextPackId: event.meta?.['contextPackId'],
                                proofLogId: event.meta?.['proofLogId'],
                                ...event.meta,
                            },
                            actions: isSuccess
                                ? ['View Results', 'View Proof Log']
                                : ['View Logs', 'Retry', 'Debug'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 25,
                    },
                    shouldInject: true,
                };
            }

            case 'bee.run':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: event.title,
                            description: event.message || 'Bee execution in progress',
                            status: event.severity === 'error' ? 'failed' : 'running',
                            data: {
                                beeName: event.meta?.['beeName'],
                                ...event.meta,
                            },
                            actions: ['View Details'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 10,
                    },
                    shouldInject: true,
                };

            case 'pr.open':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: 'Pull Request Created',
                            description: event.message || event.title,
                            status: 'completed',
                            data: {
                                url: event.meta?.['url'],
                                number: event.meta?.['number'],
                                title: event.meta?.['prTitle'] || event.title,
                                branch: event.meta?.['branch'],
                            },
                            actions: ['View PR', 'Review Changes'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 30,
                    },
                    shouldInject: true,
                };

            case 'deploy.start':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: 'Deployment Started',
                            description: event.message || event.title,
                            status: 'running',
                            data: {
                                environment: event.meta?.['environment'],
                                ...event.meta,
                            },
                            actions: ['View Progress'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 15,
                    },
                    shouldInject: true,
                };

            case 'deploy.finish': {
                const deploySuccess = event.severity !== 'error';
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: deploySuccess ? 'Deployment Successful' : 'Deployment Failed',
                            description: event.message || event.title,
                            status: deploySuccess ? 'completed' : 'failed',
                            data: {
                                environment: event.meta?.['environment'],
                                url: event.meta?.['url'],
                                ...event.meta,
                            },
                            actions: deploySuccess
                                ? ['View App', 'Check Logs']
                                : ['View Logs', 'Retry'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 25,
                    },
                    shouldInject: true,
                };
            }

            case 'test.run': {
                const testSuccess = event.severity !== 'error';
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: testSuccess ? 'Tests Passed' : 'Tests Failed',
                            description: event.message || event.title,
                            status: testSuccess ? 'completed' : 'failed',
                            data: event.meta,
                            actions: testSuccess ? ['View Results'] : ['View Logs', 'Debug'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 20,
                    },
                    shouldInject: true,
                };
            }

            case 'artifact':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: event.title || 'Artifact Created',
                            description: event.message || 'New artifact available',
                            status: 'completed',
                            data: {
                                // ProofLogPayload enrichments
                                verificationDepth: event.meta?.['verificationDepth'],
                                executionPolicy: event.meta?.['executionPolicy'],
                                modelTierUsed: event.meta?.['modelTierUsed'],
                                contextPackId: event.meta?.['contextPackId'],
                                proofLogId: event.meta?.['proofLogId'],
                                ...event.meta,
                            },
                            actions: ['View Artifact'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 15,
                    },
                    shouldInject: true,
                };

            case 'error':
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: event.title || 'Error',
                            description: event.message || 'An error occurred',
                            status: 'failed',
                            data: event.meta,
                            actions: ['View Details', 'Debug'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 10,
                    },
                    shouldInject: true,
                };

            case 'step':
            case 'info':
            case 'system':
            case 'file.read':
            case 'file.write':
            case 'command.exec':
            default:
                // For less critical events, render as simple info cards
                return {
                    message: {
                        ...baseMessage,
                        content: JSON.stringify({
                            title: event.title,
                            description: event.message || `${event.t} event`,
                            status:
                                event.severity === 'error'
                                    ? 'failed'
                                    : event.severity === 'warn'
                                      ? 'pending'
                                      : 'completed',
                            data: event.meta,
                            actions: ['View Details'],
                        }),
                        type: 'pluginCard',
                        buildCredits: 5,
                    },
                    shouldInject: true,
                };
        }
    }

    /**
     * Fetch recent missions from the backend.
     * Returns the most recent mission ID or null if none found.
     * Uses studioFetch for proper authentication (X-HiveCore-Internal-Token header).
     */
    private async fetchRecentMission(): Promise<string | null> {
        try {
            // Use studioFetch for authenticated requests to /api/studio/* endpoints
            const response = await studioFetch('/api/studio/missions/recent?limit=1', {
                method: 'GET',
            });

            if (!response.ok) {
                if (response.status === 429) {
                    console.debug('[ARCOSWebhookHandler] Rate limited on recent missions fetch');
                    throw { status: 429 };
                }
                // Log auth failures for debugging
                if (response.status === 401 || response.status === 403) {
                    console.debug(
                        '[ARCOSWebhookHandler] Auth failed on recent missions fetch:',
                        response.status
                    );
                }
                return null;
            }

            const data = await response.json();
            if (data.success && data.missions?.length > 0) {
                return data.missions[0].missionId;
            }
            return null;
        } catch (err) {
            if ((err as { status?: number })?.status === 429) {
                throw err;
            }
            console.debug('[ARCOSWebhookHandler] Failed to fetch recent missions:', err);
            return null;
        }
    }

    /**
     * Fetch timeline events for a specific mission.
     * Uses studioFetch for proper authentication (X-HiveCore-Internal-Token header).
     */
    private async fetchMissionTimeline(
        missionId: string,
        limit: number = 50
    ): Promise<MissionTimelineEvent[]> {
        try {
            // Use studioFetch for authenticated requests to /api/studio/* endpoints
            const response = await studioFetch(
                `/api/studio/missions/${missionId}/timeline-events?limit=${limit}`,
                {
                    method: 'GET',
                }
            );

            if (!response.ok) {
                if (response.status === 429) {
                    console.debug('[ARCOSWebhookHandler] Rate limited on timeline fetch');
                    throw { status: 429 };
                }
                // Log auth failures for debugging
                if (response.status === 401 || response.status === 403) {
                    console.debug(
                        '[ARCOSWebhookHandler] Auth failed on timeline fetch:',
                        response.status
                    );
                }
                return [];
            }

            const data = await response.json();
            if (data.success && Array.isArray(data.events)) {
                return data.events;
            }
            return [];
        } catch (err) {
            if ((err as { status?: number })?.status === 429) {
                throw err;
            }
            console.debug('[ARCOSWebhookHandler] Failed to fetch mission timeline:', err);
            return [];
        }
    }

    /**
     * Subscribe to real mission events via Socket.IO push (Phase 3C).
     * Returns a cleanup function that MUST be called on unmount to prevent memory leaks.
     *
     * Phase 3C SceneStream Push:
     * - Uses Socket.IO push instead of REST polling to eliminate 429s
     * - One subscription per browser session (via StudioEventStreamService singleton)
     * - Dedup by eventId (handled by StudioEventStreamService)
     * - Backfill on reconnect using after=<lastEventId>
     * - REST calls only for: initial load, session switch, reconnect, "load older"
     *
     * When VITE_ENABLE_UI_MOCK_EVENTS=true, uses mock events instead for UI demonstration.
     */
    listenForWebhooks(
        _sessionId: string,
        onEvent: (result: WebhookResult) => void
    ): WebhookCleanup {
        // Phase 3C Debug: Log listenForWebhooks invocation when VITE_SCENESTREAM_DEBUG=true
        const sceneStreamDebug = (import.meta as any).env?.['VITE_SCENESTREAM_DEBUG'] === 'true';
        if (sceneStreamDebug) {
            console.log('[Phase3C] arcWebhookHandler.listenForWebhooks() invoked', {
                sessionId: _sessionId,
                mockEventsEnabled: isMockEventsEnabled(),
            });
        }

        // If mock events are enabled, use the mock event timeline
        if (isMockEventsEnabled()) {
            return this.listenForMockWebhooks(onEvent);
        }

        // Phase 3C: Use Socket.IO push instead of REST polling
        // Subscribe to mission events via StudioEventStreamService
        const unsubscribe = studioEventStream.subscribe((event: StreamMissionEvent) => {
            // Map the Socket.IO event to the existing card format
            const mappedEvent: MissionTimelineEvent = {
                id: event.id,
                missionId: event.missionId,
                t: event.t as TimelineEventType,
                severity: event.severity,
                title: event.title,
                message: event.message,
                meta: event.meta as Record<string, unknown>,
                createdAt: event.createdAt,
            };

            const result = this.mapTimelineEventToCard(mappedEvent);
            if (result.shouldInject) {
                onEvent(result);
            }
        });

        // Connect to Socket.IO (idempotent - will reuse existing connection)
        studioEventStream.connect();

        // Return cleanup function
        return () => {
            unsubscribe();
            // Note: We don't disconnect the socket here because other components
            // may still be using it. The socket will be cleaned up on page unload.
        };
    }

    /**
     * Mock event listener for UI demonstration (when VITE_ENABLE_UI_MOCK_EVENTS=true).
     * Simulates a mission lifecycle with timed events.
     */
    private listenForMockWebhooks(onEvent: (result: WebhookResult) => void): WebhookCleanup {
        const timeoutIds: ReturnType<typeof setTimeout>[] = [];

        const mockEvents = [
            {
                type: 'task_completed' as const,
                delay: 3000,
                data: {
                    summary: 'Mission initialized - PENDING state',
                    beeName: 'MissionBridge',
                    verificationDepth: 'standard',
                    executionPolicy: 'auto-approve',
                    source: 'mock',
                },
            },
            {
                type: 'pr_created' as const,
                delay: 6000,
                data: {
                    number: 42,
                    title: 'feat: implement color card rendering',
                    url: 'https://github.com/DDPMLS/beehive-core/pull/42',
                    branch: 'devin/phase3-event-listeners',
                    source: 'mock',
                },
            },
            {
                type: 'ci_status' as const,
                delay: 10000,
                data: {
                    status: 'success',
                    workflow: 'CI/CD Pipeline',
                    duration: '2m 34s',
                    url: 'https://github.com/DDPMLS/beehive-core/actions/runs/123',
                    modelTierUsed: 'gpt-4',
                    contextPackId: 'ctx_abc123',
                    source: 'mock',
                },
            },
            {
                type: 'deployment_success' as const,
                delay: 14000,
                data: {
                    environment: 'staging',
                    url: 'https://staging.beehive-core.com',
                    deploymentId: 'deploy_456',
                    duration: '45s',
                    source: 'mock',
                },
            },
            {
                type: 'task_completed' as const,
                delay: 18000,
                data: {
                    summary: 'Mission COMPLETED - all checks passed',
                    beeName: 'OutcomeTracking',
                    verificationDepth: 'deep',
                    executionPolicy: 'auto-approve',
                    proofLogId: 'proof_789',
                    source: 'mock',
                },
            },
        ];

        mockEvents.forEach(({ type, delay, data }) => {
            const timeoutId = setTimeout(async () => {
                const result = await this.simulateWebhookEvent(type, data);
                onEvent(result);
            }, delay);
            timeoutIds.push(timeoutId);
        });

        return () => {
            timeoutIds.forEach(id => clearTimeout(id));
        };
    }
}

export const arcWebhookHandler = new ARCOSWebhookHandler();
